import type { IOtaRoomTypeFormSchema } from "./NewItem"
import type { TOtaRoomTypeWithMedia } from "./store"

// the backend manages base children, so it is always sent as 0
export const OTA_BASE_CHILD_OCCUPANCY = 0

export const emptyOtaRoomTypeFormValues: IOtaRoomTypeFormSchema = {
	name: "",
	no_of_rooms: undefined,
	base_adult_occupancy: undefined,
	max_occupancy: undefined,
	max_adult_occupancy: undefined,
	max_child_occupancy: undefined,
	image: undefined,
	applicable_meal_plans: "all",
	ota_meal_plans: [],
	amenities: [],
}

export function otaRoomTypeToFormValues(
	roomType: TOtaRoomTypeWithMedia
): IOtaRoomTypeFormSchema {
	return {
		name: roomType.name,
		no_of_rooms: String(roomType.no_of_rooms),
		base_adult_occupancy: String(roomType.base_adult_occupancy),
		max_occupancy: String(roomType.max_occupancy),
		max_adult_occupancy: String(roomType.max_adult_occupancy),
		max_child_occupancy: String(roomType.max_child_occupancy),
		image: undefined,
		applicable_meal_plans: roomType.includes_all_meal_plans
			? "all"
			: "specific",
		ota_meal_plans: roomType.ota_meal_plans.map((mp) => mp.id),
		amenities: roomType.amenities.map(({ key, name }) => ({ key, name })),
	}
}

// Nested values use bracket keys (`amenities[0][key]`, `amenities[0][name]`, `ota_room_types[0][image]`)
// booleans are sent as 1/0 and nulls as "" (the api reads empty strings as null).
function appendToFormData(data: FormData, key: string, value: unknown) {
	if (value === undefined) return
	if (value === null) {
		data.append(key, "")
	} else if (value instanceof Blob) {
		data.append(key, value)
	} else if (Array.isArray(value)) {
		value.forEach((item, index) =>
			appendToFormData(data, `${key}[${index}]`, item)
		)
	} else if (typeof value === "boolean") {
		data.append(key, value ? "1" : "0")
	} else if (typeof value === "object") {
		Object.entries(value).forEach(([childKey, childValue]) =>
			appendToFormData(data, key ? `${key}[${childKey}]` : childKey, childValue)
		)
	} else {
		data.append(key, String(value))
	}
}

/**
 * Request body as multipart form data, for the endpoints that take an image
 * along with the rest of the fields.
 */
function payloadToFormData(payload: object): FormData {
	const data = new FormData()
	appendToFormData(data, "", payload)
	return data
}

export function otaRoomTypeFormValuesToFormData(
	values: IOtaRoomTypeFormSchema
) {
	const includesAllMealPlans = values.applicable_meal_plans === "all"
	return payloadToFormData({
		name: values.name,
		no_of_rooms: values.no_of_rooms,
		base_adult_occupancy: values.base_adult_occupancy,
		base_child_occupancy: OTA_BASE_CHILD_OCCUPANCY,
		max_occupancy: values.max_occupancy,
		max_adult_occupancy: values.max_adult_occupancy,
		max_child_occupancy: values.max_child_occupancy,
		includes_all_meal_plans: includesAllMealPlans,
		ota_meal_plans: includesAllMealPlans ? undefined : values.ota_meal_plans,
		image: values.image instanceof File ? values.image : undefined,
		amenities: values.amenities.map(({ key, name }) => ({ key, name })),
	})
}
